import { useQueryClient } from '@tanstack/react-query';
import { ExternalLink, PhoneIncoming, PhoneMissed, PhoneOff, PhoneOutgoing, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { formatDuration } from '@/features/calls/call-display';
import { callKeys } from '@/features/calls/call-keys';
import { useContact } from '@/features/contacts/use-contacts';
import { useLead } from '@/features/leads/use-leads';
import { todayKeys } from '@/features/today/use-today';
import { formatRelative } from '@/lib/datetime';
import { formatPhoneDisplay } from '@/lib/phone';
import { cn } from '@/lib/utils';
import type { LiveCall } from './use-live-events';

/**
 * How long the banner stays before dismissing itself.
 *
 * Long enough to outlast a phone ringing and be noticed on a second screen,
 * short enough that a card about a call taken ten minutes ago is not still
 * sitting over the work. Dismissing early is one click; the call is on the
 * Today queue and in the bell regardless, so nothing is lost either way.
 */
const AUTO_DISMISS_MS = 90_000;

/**
 * Once the call is over, the card has done its job.
 *
 * An ANSWERED call goes quickly: somebody is on the phone and the notes have
 * been read. One that nobody picked up lingers, because that is the one with
 * work attached — it stays long enough to be seen and opened.
 */
const ANSWERED_DISMISS_MS = 12_000;
const UNANSWERED_DISMISS_MS = 60_000;

/**
 * What the card says, and for how long. Pure, so it can be tested.
 *
 * DIRECTION CHANGES THE SENTENCE, not just the icon. "Incoming call" and
 * "Calling Ravi" are different messages to a human: one is a thing
 * happening TO them that they must react to, the other is a thing they just
 * did. Reusing the inbound wording for an outbound call would read as a
 * second customer ringing in halfway through dialling the first.
 */
export function bannerState(
  settled: LiveCall['settled'],
  direction: LiveCall['direction'] = 'inbound',
): {
  status: string;
  tone: 'ringing' | 'dialling' | 'answered' | 'unanswered';
  dismissAfterMs: number;
} {
  const outbound = direction === 'outbound';

  if (!settled) {
    return outbound
      ? /*
         * FreJun rings the OPERATOR first and the customer second, so this
         * is the moment to say which phone is about to ring. Without it the
         * first thing that happens after clicking Call is your own handset
         * going off, which reads as a wrong number.
         */
        { status: 'Calling — answer your phone', tone: 'dialling', dismissAfterMs: AUTO_DISMISS_MS }
      : { status: 'Incoming call', tone: 'ringing', dismissAfterMs: AUTO_DISMISS_MS };
  }

  if (settled.answered) {
    return {
      status: `Answered · ${formatDuration(settled.durationSeconds)}`,
      tone: 'answered',
      dismissAfterMs: ANSWERED_DISMISS_MS,
    };
  }

  /*
   * The one with work attached, so it is the one that lingers and the one
   * that is red — but only inbound is a MISSED call. We rang them and they
   * did not pick up, which is a different fact and a different next step:
   * nobody is waiting on us to call back, we are waiting on them.
   */
  return outbound
    ? { status: 'No answer', tone: 'unanswered', dismissAfterMs: ANSWERED_DISMISS_MS }
    : { status: 'Missed call — call back', tone: 'unanswered', dismissAfterMs: UNANSWERED_DISMISS_MS };
}

/** At most three, because this is a glance, not the lead page. */
const NOTES_SHOWN = 3;

/**
 * "Ravi Fresh is calling" — top right, the moment the phone rings.
 *
 * The CRM had no way to tell anyone a call was happening. The lead was
 * created silently by the FreJun webhook and surfaced only when somebody
 * happened to reload a page, which on a second screen could be never.
 *
 * WHAT IT SHOWS is chosen for the three seconds before the handset is
 * picked up: who is calling, which enquiry it is about, and the last thing
 * anybody wrote down about them. The notes are the point — answering "yes
 * Ravi, about the Annanagar branch" is the difference the operator feels.
 *
 * It does NOT navigate on its own. Being thrown out of a half-typed note by
 * somebody else's call is worse than the click it saves, and on a shared
 * inbound number the person taking the call may not be the person at this
 * screen. Opening is a decision: here, or in a new tab so the current work
 * survives.
 */
/**
 * `call` arrives as a PROP rather than from the hook.
 *
 * There is one socket per tab, so a second component calling
 * `useLiveEvents` would open a second connection. The shell calls it once
 * and hands the pieces to whoever needs them.
 */
/**
 * What the call list has been told so far, as one string.
 *
 * The banner refetches when this CHANGES, so what goes in it decides how
 * often that happens — and both ways of getting it wrong are bad. Keyed on
 * the call id alone, a call that rings and then ends refetches once and the
 * list keeps a row with no outcome and no duration. Keyed on the whole
 * event, it refetches on every render of a banner that is just sitting
 * there.
 *
 * Two values per call: it arrived, and it finished.
 */
export const callPhase = (call: LiveCall | null): string | null =>
  call ? `${call.callId}:${call.settled?.outcome ?? 'live'}` : null;

export function IncomingCallBanner({ call }: { call: LiveCall | null }) {
  const navigate = useNavigate();
  const event = call;
  const [dismissed, setDismissed] = useState<number | null>(null);

  // Keyed on the call id, so a second call re-opens a banner the operator
  // had dismissed rather than being swallowed by the earlier dismissal.
  const showing = event && event.callId !== dismissed ? event : null;

  /*
   * The clock restarts when the call resolves, which is the point: a card
   * that appeared when the phone rang should not vanish mid-conversation,
   * and one for a call that ended should not sit there for another minute
   * and a half pretending to be live.
   */
  const settled = showing?.settled ?? null;
  const { status, tone, dismissAfterMs } = bannerState(settled, showing?.direction);
  useEffect(() => {
    if (!showing) return;
    const timer = window.setTimeout(() => setDismissed(showing.callId), dismissAfterMs);
    return () => window.clearTimeout(timer);
  }, [showing, dismissAfterMs]);

  /*
   * THE CALL LIST IS STALE THE MOMENT THIS BANNER MOVES.
   *
   * A call writes a row when it is placed or received, and rewrites it when
   * it ends — the outcome, the duration, and a moment later the recording.
   * Nothing told the Calls page any of that: it fetched once and sat there,
   * so an operator could watch a call arrive on the banner, switch to Calls,
   * and be looking at a list that did not contain it.
   *
   * `phase` is the two transitions that change the row — the call appearing,
   * and it settling — so this fires twice per call rather than on every
   * render of a banner that is already up.
   */
  const qc = useQueryClient();
  const phase = callPhase(showing);

  useEffect(() => {
    if (!phase) return;
    void qc.invalidateQueries({ queryKey: callKeys.all });
    // A missed call becomes a callback row, which is Today's first block.
    void qc.invalidateQueries({ queryKey: todayKeys.all });
  }, [phase, qc]);

  /*
   * And once more when the banner goes away.
   *
   * The recording URL arrives on its own webhook seconds after the call
   * ends, so the refetch at settle time is often too early to see it — and
   * the Listen button is the one thing on a call row that needs it. The ref
   * is what keeps this from firing on first mount, when there has never been
   * a banner to close.
   */
  const wasShowing = useRef(false);
  useEffect(() => {
    if (showing) {
      wasShowing.current = true;
      return;
    }
    if (!wasShowing.current) return;
    wasShowing.current = false;
    void qc.invalidateQueries({ queryKey: callKeys.all });
    void qc.invalidateQueries({ queryKey: todayKeys.all });
  }, [showing, qc]);

  /*
   * The lead carries the contact AND the recent notes in one row — the same
   * read the leads list uses — so the common case is a single request. The
   * contact is only fetched when the call could not be filed against a lead,
   * which happens when the customer has several open.
   */
  const lead = useLead(showing?.leadId ?? undefined);
  const contact = useContact(showing && !showing.leadId ? showing.contactId : undefined);

  if (!showing) return null;

  /*
   * Three states, one card. The heading is the whole message — an operator
   * glancing across the room reads "Missed call" or "Answered", not a
   * duration — so the wording changes rather than a badge being added.
   */
  const Icon =
    tone === 'ringing'
      ? PhoneIncoming
      : tone === 'dialling'
        ? PhoneOutgoing
        : tone === 'answered'
          ? PhoneOff
          : PhoneMissed;

  const who = lead.data?.contact.fullName ?? contact.data?.fullName ?? null;
  const phone = lead.data?.contact.phoneE164 ?? contact.data?.phoneE164 ?? null;
  const notes = (lead.data?.recentNotes ?? []).slice(0, NOTES_SHOWN);
  const href = showing.leadId ? `/leads/${showing.leadId}` : `/contacts/${showing.contactId}`;

  return (
    /*
     * Fixed, top right, under the dialogs. z-40 matches the New lead button:
     * a modal must cover this, and it must cover the page. On a phone it
     * spans the width instead, because a 24rem card beside a 20rem screen is
     * a card nobody can read.
     */
    <aside
      aria-label={status}
      className={cn(
        'fixed inset-x-4 top-20 z-40 rounded-[var(--radius-card)] border bg-surface shadow-xl sm:inset-x-auto sm:right-4 sm:top-20 sm:w-[22rem] print:hidden',
        tone === 'ringing' && 'border-brand/40',
        tone === 'dialling' && 'border-info/40',
        tone === 'answered' && 'border-ok/40',
        tone === 'unanswered' && 'border-danger/40',
      )}
    >
      <div className="flex items-start gap-3 border-b border-line p-4">
        {/*
          `animate-pulse` is the one place a moving thing is right: it says
          "now" in a way a static icon cannot — and it STOPS the moment the
          call does, which is half of how this reads as live rather than as
          a notification that happens to be recent.
        */}
        <span
          className={cn(
            'mt-0.5 grid size-9 shrink-0 place-items-center rounded-full',
            tone === 'ringing' && 'animate-pulse bg-brand-soft text-brand',
            // Dialling pulses too — the phone really is ringing — but in the
            // quieter neutral, because this is a call the operator started
            // and not one arriving at them.
            tone === 'dialling' && 'animate-pulse bg-info-soft text-info',
            tone === 'answered' && 'bg-ok-soft text-ok',
            tone === 'unanswered' && 'bg-danger-soft text-danger',
          )}
        >
          <Icon className="size-[18px]" aria-hidden="true" />
        </span>

        <div className="min-w-0 flex-1">
          <p
            className={cn(
              'text-[11px] font-bold uppercase tracking-wider',
              tone === 'ringing' && 'text-brand',
              tone === 'dialling' && 'text-info',
              tone === 'answered' && 'text-ok',
              tone === 'unanswered' && 'text-danger',
            )}
          >
            {status}
          </p>
          <p className="truncate text-[15px] font-bold text-ink">
            {who ?? (phone ? formatPhoneDisplay(phone) : 'Unknown caller')}
          </p>
          {/* The lead, because a customer may hold several and "call Ravi"
              is not something anyone can act on without knowing which. */}
          <p className="truncate text-[13px] text-muted">
            {lead.data?.name ??
              (showing.leadId
                ? 'Loading…'
                : // "New caller" is an inbound sentence. Nobody rang us.
                  showing.direction === 'outbound'
                  ? 'No lead linked'
                  : 'No lead — new caller')}
            {who && phone && ` · ${formatPhoneDisplay(phone)}`}
          </p>
        </div>

        <button
          type="button"
          onClick={() => setDismissed(showing.callId)}
          aria-label="Dismiss"
          className="-mr-1 -mt-1 rounded-[var(--radius-control)] p-1.5 text-muted transition-colors hover:bg-surface-2 hover:text-ink"
        >
          <X className="size-4" aria-hidden="true" />
        </button>
      </div>

      {notes.length > 0 && (
        <ul className="max-h-40 divide-y divide-line overflow-y-auto">
          {notes.map((n) => (
            <li key={n.id} className="px-4 py-2.5">
              <p className="text-[11px] text-muted">
                {n.authorName ?? 'Unknown'} · {formatRelative(n.createdAt)}
              </p>
              <p className="mt-0.5 line-clamp-3 whitespace-pre-wrap break-words text-[13px] leading-relaxed text-ink-2">
                {n.body}
                {n.truncated && '…'}
              </p>
            </li>
          ))}
        </ul>
      )}

      {lead.data && notes.length === 0 && (
        <p className="px-4 py-3 text-[13px] text-muted">Nothing written about them yet.</p>
      )}

      <div className="flex gap-2 border-t border-line p-3">
        <Button
          size="sm"
          className="flex-1"
          onClick={() => {
            setDismissed(showing.callId);
            navigate(href);
          }}
        >
          Open
        </Button>
        {/*
          A real anchor, not `window.open`: middle-click, ⌘-click and "open
          in new window" all work, and a popup blocker has nothing to block.
          `noreferrer` with `_blank` so the new tab cannot reach back through
          `window.opener`.
        */}
        <Button asChild variant="secondary" size="sm">
          <a href={href} target="_blank" rel="noreferrer" onClick={() => setDismissed(showing.callId)}>
            <ExternalLink /> New tab
          </a>
        </Button>
      </div>
    </aside>
  );
}
